import React from "react";
import { Card, CardContent, Box, Typography, Chip, useTheme } from "@mui/material";
import { CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { formatDate } from "../../../../utils/dateUtils";
import { SECURITY_REVIEW_STATUS } from "../../utils/constants";
import { getOwnerLabel } from "../../hooks/useVendorStaff";

const statusChip = (theme, status) => {
  if (status === SECURITY_REVIEW_STATUS.DRAFT) {
    return {
      color: theme.customColors?.status?.warning?.text || "#D97706",
      bg: theme.customColors?.status?.warning?.bg || "#FEF3C7",
      icon: "Schedule",
      label: "Draft",
    };
  }
  if (status === SECURITY_REVIEW_STATUS.COMPLETED) {
    return {
      color: theme.customColors?.status?.success?.text || "#059669",
      bg: theme.customColors?.status?.success?.bg || "#D1FAE5",
      icon: "Check",
      label: "Completed",
    };
  }
  if (status === SECURITY_REVIEW_STATUS.DISCARDED) {
    return {
      color: theme.customColors?.status?.error?.text || "#DC2626",
      bg: theme.customColors?.status?.error?.bg || "#FEE2E2",
      icon: null,
      label: "Discarded",
    };
  }
  return {
    color: theme.palette.text.primary,
    bg: theme.palette.background.paper,
    icon: null,
    label: status,
  };
};

const ReviewStatusCard = ({ review, vendor, people = [] }) => {
  const theme = useTheme();
  if (!review) return null;

  const ownerName = getOwnerLabel(people, review.owner || vendor.owner);
  const meta = statusChip(theme, review.status);

  const title =
    review.status === SECURITY_REVIEW_STATUS.COMPLETED
      ? `Security Review approved at ${formatDate(review.completedAt)}`
      : review.status === SECURITY_REVIEW_STATUS.DISCARDED
        ? `Security Review discarded at ${formatDate(review.discardedAt)}`
        : `Security Review started at ${formatDate(review.createdAt)}`;

  const subtitle =
    review.status === SECURITY_REVIEW_STATUS.DRAFT
      ? null
      : `Started at ${formatDate(review.createdAt)}`;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 0,
        backgroundColor: theme.customColors?.background?.cardHeader || "#F9FAFB",
        border: `1px solid ${theme.customColors?.border?.subtle || "#E5E7EB"}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ color: theme.customColors?.text?.strong || "#111827" }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{ color: theme.customColors?.text?.secondary || "#6B7280", mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{ color: theme.customColors?.text?.secondary || "#6B7280", mt: 0.5 }}
            >
              Vendor: {vendor.name} | Created by: {ownerName}
            </Typography>
          </Box>
          <Chip
            label={meta.label}
            icon={
              meta.icon === "Schedule" ? (
                <ScheduleIcon sx={{ fontSize: 16 }} />
              ) : meta.icon === "Check" ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} />
              ) : null
            }
            size="small"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: 1,
              border: "none",
              minWidth: 90,
              textAlign: "center",
              backgroundColor: meta.bg,
              color: meta.color,
              "& .MuiChip-icon": { color: "inherit", fontSize: 16 },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReviewStatusCard;
